import type { components } from "@/api-schema";
import { Action, UserType } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { useUserActivateGuestMutation } from "@/common/api/user/use-user-activate-guest.mutation";
import { useUserDeactivateGuestMutation } from "@/common/api/user/use-user-deactivate-guest.mutation";
import { useUserReInviteGuestMutation } from "@/common/api/user/use-user-re-invite-guest.mutation";
import { getFullUserName } from "@/common/helpers/get-full-user-name";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { userIsEditableGuest } from "@/common/helpers/user-is-editable-guest";
import { BaseConfirmDialog } from "@/components/confirm-dialog/base-confirm-dialog";
import type { DetailAction } from "@/components/detail-page/types";
import { ButtonColorVariant } from "@/components/ui/button";
import { MailIcon, PencilIcon, UserRoundCheck, UserRoundMinus } from "lucide-react";
import { useCallback, useMemo, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

type User = components["schemas"]["User"];

interface UseUserActionsReturn {
  getActionsForUser: (user: User) => DetailAction[];
  dialogs: JSX.Element;
}

export function useUserActions(): UseUserActionsReturn {
  const { t } = useTranslation(["common", "masterdata"]);
  const [, navigate] = useLocation();
  const ability = useAbility();
  const canManageUser = ability.can(Action.Manage, "User");

  const { mutateAsync: reInviteGuest } = useUserReInviteGuestMutation();
  const { mutateAsync: deactivateGuest, isPending: isDeactivating } = useUserDeactivateGuestMutation();
  const { mutateAsync: activateGuest } = useUserActivateGuestMutation();

  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);

  const handleReInvite = useCallback(
    async (user: User) => {
      try {
        await toastMutationStatus({
          mutationFn: () => reInviteGuest({ uuid: user.uuid }),
          successMessage: t("masterdata:InvitationSentAgain"),
          errorMessage: t("masterdata:ErrorResendingInvitation"),
        });
      } catch (error) {
        console.error("Error inviting guest user:", error);
      }
    },
    [reInviteGuest, t],
  );

  const handleDeactivate = useCallback(
    async (user: User) => {
      try {
        await toastMutationStatus({
          mutationFn: () => deactivateGuest({ uuid: user.uuid }),
          successMessage: t("masterdata:UserDeactivated", { name: getFullUserName({ user }) }),
          errorMessage: t("masterdata:ErrorDeactivatingUser"),
        });
      } catch (error) {
        console.error("Error deactivating guest user with uuid - ", user.uuid, error);
      }
    },
    [deactivateGuest, t],
  );

  const handleActivate = useCallback(
    async (user: User) => {
      try {
        await toastMutationStatus({
          mutationFn: () => activateGuest({ uuid: user.uuid }),
          successMessage: t("masterdata:UserActivated", { name: getFullUserName({ user }) }),
          errorMessage: t("masterdata:ErrorActivatingUser"),
        });
      } catch (error) {
        console.error("Error activating guest user with uuid - ", user.uuid, error);
      }
    },
    [activateGuest, t],
  );

  const getActionsForUser = useCallback(
    (user: User): DetailAction[] => {
      if (!canManageUser || user.type !== UserType.Guest) {
        return [];
      }

      if (!userIsEditableGuest(user)) {
        return [
          {
            id: "activate",
            label: t("masterdata:Activate"),
            icon: UserRoundCheck,
            onClick: () => handleActivate(user),
          },
        ];
      }

      return [
        {
          id: "resend-invitation",
          label: t("masterdata:ResendInvitation"),
          icon: MailIcon,
          onClick: () => handleReInvite(user),
        },
        {
          id: "deactivate",
          label: t("masterdata:Deactivate"),
          icon: UserRoundMinus,
          destructive: true,
          onClick: () => {
            setUserToDeactivate(user);
            setIsDeactivateDialogOpen(true);
          },
        },
        {
          id: "edit",
          label: t("common:Edit"),
          icon: PencilIcon,
          onClick: () => navigate(`~/masterdata/user/${user.uuid}/update`),
        },
      ];
    },
    [canManageUser, handleActivate, handleReInvite, navigate, t],
  );

  const handleConfirmDeactivate = useCallback(async () => {
    if (!userToDeactivate || isDeactivating) return;

    await handleDeactivate(userToDeactivate);
    setIsDeactivateDialogOpen(false);
  }, [handleDeactivate, isDeactivating, userToDeactivate]);

  const dialogs = useMemo(
    () => (
      <BaseConfirmDialog
        open={isDeactivateDialogOpen}
        title={t("masterdata:DeactivateGuest")}
        description={t("masterdata:DeactivateGuestDescription", {
          name: userToDeactivate ? getFullUserName({ user: userToDeactivate }) : "",
        })}
        labelCancel={t("common:NoCancel")}
        labelConfirm={t("masterdata:Deactivate")}
        confirmColorVariant={ButtonColorVariant.Error}
        onCancel={() => setIsDeactivateDialogOpen(false)}
        onConfirm={handleConfirmDeactivate}
      />
    ),
    [handleConfirmDeactivate, isDeactivateDialogOpen, t, userToDeactivate],
  );

  return { getActionsForUser, dialogs };
}
